// TelcoTrack - Shared Module (Simplified)
// Contains essential reusable components

import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

// Essential UI Components
import { ButtonComponent } from './components/ui/button/button.component';
import { EditIconButtonComponent } from './components/ui/edit-icon-button/edit-icon-button.component';
import { DeleteIconButtonComponent } from './components/ui/delete-icon-button/delete-icon-button.component';
import { DownloadIconButtonComponent } from './components/ui/download-icon-button/download-icon-button.component';
import { PhotoGridComponent } from './components/ui/photo-grid/photo-grid.component';
import { ConfirmIconButtonComponent } from './components/ui/confirm-icon-button/confirm-icon-button.component';
import { AddIconButtonComponent } from './components/ui/add-icon-button/add-icon-button.component';
import { TransferIconButtonComponent } from './components/ui/transfer-icon-button/transfer-icon-button.component';
import { IconButtonComponent } from './components/ui/icon-button/icon-button.component';
import { ActionIconComponent } from './components/ui/icons/action-icon.component';
import { VocabIconComponent } from './components/ui/icons/vocab-icon.component';
import { InputComponent } from './components/ui/input/input.component';
import { SpinnerComponent } from './components/ui/spinner/spinner.component';
import { CardComponent } from './components/ui/card/card.component';
import { ModalComponent } from './components/ui/modal/modal.component';
import { ZoomablePhotoComponent } from './components/ui/zoomable-photo/zoomable-photo.component';
import { StatusBadgeComponent } from './components/ui/status-badge/status-badge.component';
import { PaymentBadgeComponent } from './components/ui/payment-badge/payment-badge.component';
import { EmptyStateComponent } from './components/ui/empty-state/empty-state.component';
import { ErrorStateComponent } from './components/ui/error-state/error-state.component';
import { LoadingStateComponent } from './components/ui/loading-state/loading-state.component';
import { SkeletonComponent } from './components/ui/skeleton/skeleton.component';
import { FilterMultiSelectComponent } from './components/ui/filter-multi-select/filter-multi-select.component';
import { FilterPolygonPlanStatusComponent } from './components/ui/filter-polygon-plan-status/filter-polygon-plan-status.component';
import { TablePaginationComponent } from './components/ui/table-pagination/table-pagination.component';
import { DataTableComponent } from './components/ui/data-table/data-table.component';
import { DataTableCellDirective } from './components/ui/data-table/data-table-cell.directive';
import { DataTableExpandDirective } from './components/ui/data-table/data-table-expand.directive';
import { DataTableHeaderCellDirective } from './components/ui/data-table/data-table-header-cell.directive';
import { DetailsCloseOnOutsideClickDirective } from './components/ui/details-close-on-outside-click.directive';
import { DetailSectionComponent } from './components/ui/detail-section/detail-section.component';
import { DetailItemComponent } from './components/ui/detail-section/detail-item.component';
import { DetailValueBlankDirective } from './components/ui/detail-section/detail-value-blank.directive';
import { ActivitySummaryBlockComponent } from './components/activity-summary-block/activity-summary-block.component';
import { ActivityPhotoThumbsComponent } from './components/activity-photo-thumbs/activity-photo-thumbs.component';
import { PageHeaderComponent } from './components/ui/page-header/page-header.component';
import { PageGuideComponent } from './components/ui/page-guide/page-guide.component';
import { TabsComponent } from './components/ui/tabs/tabs.component';
import { ChartRangeControlsComponent } from './components/ui/chart-range-controls/chart-range-controls.component';
import { FilterDateRangeComponent } from './components/ui/filter-date-range/filter-date-range.component';
import { ObjectAttributeFilterComponent } from './components/ui/object-attribute-filter/object-attribute-filter.component';
import { StepperComponent } from './components/ui/stepper/stepper.component';
import { ToastHostComponent } from './components/ui/toast/toast-host.component';
import { TopbarSectionTabsDirective } from './directives/topbar-section-tabs.directive';
import { TopbarPageTitleDirective } from './directives/topbar-page-title.directive';
import { PageHeaderFiltersDirective } from './directives/page-header-filters.directive';
import { PageHeaderTableToolsDirective } from './directives/page-header-table-tools.directive';
import { PageHeaderPillsDirective } from './directives/page-header-pills.directive';
import { ResizableTableDirective } from './directives/resizable-table.directive';
import { CountUpDirective } from './directives/count-up.directive';

// Feature Components
import { ImportModalComponent } from './components/import-modal/import-modal.component';
import { ImportLayerMapPreviewComponent } from './components/import-layer-map-preview/import-layer-map-preview.component';
import { FinanceSummaryComponent } from './components/finance-summary/finance-summary.component';
import { FinanceProfitabilityChartComponent } from './components/finance-profitability-chart/finance-profitability-chart.component';
import { FinanceProgressComparisonComponent } from './components/finance-progress-comparison/finance-progress-comparison.component';
import { MaterialsPanelComponent } from './components/materials-panel/materials-panel.component';
import { ProductCategoryTreeComponent } from './components/product-category-tree/product-category-tree.component';

// Services

@NgModule({
  declarations: [
    ButtonComponent,
    EditIconButtonComponent,
    DeleteIconButtonComponent,
    DownloadIconButtonComponent,
    PhotoGridComponent,
    ConfirmIconButtonComponent,
    AddIconButtonComponent,
    TransferIconButtonComponent,
    IconButtonComponent,
    ActionIconComponent,
    VocabIconComponent,
    InputComponent,
    SpinnerComponent,
    CardComponent,
    ModalComponent,
    ZoomablePhotoComponent,
    StatusBadgeComponent,
    PaymentBadgeComponent,
    EmptyStateComponent,
    ErrorStateComponent,
    LoadingStateComponent,
    SkeletonComponent,
    ImportModalComponent,
    ImportLayerMapPreviewComponent,
    FilterMultiSelectComponent,
    FilterPolygonPlanStatusComponent,
    TablePaginationComponent,
    DataTableComponent,
    DataTableCellDirective,
    DataTableExpandDirective,
    DataTableHeaderCellDirective,
    DetailsCloseOnOutsideClickDirective,
    DetailSectionComponent,
    DetailItemComponent,
    DetailValueBlankDirective,
    ActivitySummaryBlockComponent,
    ActivityPhotoThumbsComponent,
    FinanceSummaryComponent,
    FinanceProfitabilityChartComponent,
    FinanceProgressComparisonComponent,
    MaterialsPanelComponent,
    ProductCategoryTreeComponent,
    PageHeaderComponent,
    PageGuideComponent,
    TabsComponent,
    ChartRangeControlsComponent,
    FilterDateRangeComponent,
    ObjectAttributeFilterComponent,
    StepperComponent,
    ToastHostComponent,
    TopbarSectionTabsDirective,
    TopbarPageTitleDirective,
    PageHeaderFiltersDirective,
    PageHeaderTableToolsDirective,
    PageHeaderPillsDirective,
    ResizableTableDirective,
  ],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    RouterModule,
    CountUpDirective,
  ],
  providers: [
  ],
  exports: [
    // Re-export Angular modules for convenience
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    RouterModule,
    
    // Export our components
    ButtonComponent,
    EditIconButtonComponent,
    DeleteIconButtonComponent,
    DownloadIconButtonComponent,
    PhotoGridComponent,
    ConfirmIconButtonComponent,
    AddIconButtonComponent,
    TransferIconButtonComponent,
    IconButtonComponent,
    ActionIconComponent,
    VocabIconComponent,
    InputComponent,
    SpinnerComponent,
    CardComponent,
    ModalComponent,
    ZoomablePhotoComponent,
    StatusBadgeComponent,
    PaymentBadgeComponent,
    EmptyStateComponent,
    ErrorStateComponent,
    LoadingStateComponent,
    SkeletonComponent,
    ImportModalComponent,
    ImportLayerMapPreviewComponent,
    FilterMultiSelectComponent,
    FilterPolygonPlanStatusComponent,
    TablePaginationComponent,
    DataTableComponent,
    DataTableCellDirective,
    DataTableExpandDirective,
    DataTableHeaderCellDirective,
    DetailsCloseOnOutsideClickDirective,
    DetailSectionComponent,
    DetailItemComponent,
    DetailValueBlankDirective,
    ActivitySummaryBlockComponent,
    ActivityPhotoThumbsComponent,
    FinanceSummaryComponent,
    FinanceProfitabilityChartComponent,
    FinanceProgressComparisonComponent,
    MaterialsPanelComponent,
    ProductCategoryTreeComponent,
    PageHeaderComponent,
    PageGuideComponent,
    TabsComponent,
    ChartRangeControlsComponent,
    FilterDateRangeComponent,
    ObjectAttributeFilterComponent,
    StepperComponent,
    ToastHostComponent,
    TopbarSectionTabsDirective,
    TopbarPageTitleDirective,
    PageHeaderFiltersDirective,
    PageHeaderTableToolsDirective,
    PageHeaderPillsDirective,
    ResizableTableDirective,
    CountUpDirective,
  ]
})
export class SharedModule { }
